import type {ContentReviewDto} from '@devmil/contracts';
import {useEffect, useId, useRef, useState} from 'react';

import {usePanel} from '../../app/PanelProvider.js';

const MIN_COMMENT = 3;
const MAX_CHANGE_COMMENT = 500;
/** Rejeitar é terminal, então o motivo pode ser mais longo. */
const MAX_REJECT_COMMENT = 1000;
/** A régua do contrato: `ApprovalJustificationSchema` aceita até 1000. */
const MAX_APPROVE_JUSTIFICATION = 1000;
/** A MESMA régua da justificativa: `RegenerateContentInputSchema`, 3 a 1000. */
const MAX_REGENERATE_REASON = 1000;

type Decision = 'approve' | 'change' | 'reject' | 'regenerate';

/**
 * O porquê, dito na tela onde a decisão acontece.
 *
 * Sem esta frase, a caixa aparece do nada e o dono não sabe se escreveu demais,
 * de menos, ou se clicou no botão errado. Ela repete o motivo da API de
 * propósito: a recusa de lá tem esta mesma causa.
 */
const MOTIVO_DA_RESSALVA =
  'há alerta nos portões, e aprovar exige justificativa escrita';

/**
 * O que regenerar faz, dito antes do clique que faz.
 *
 * Regenerar é a única ação desta tela que JOGA FORA a versão que está sendo
 * lida e manda a esteira escrever outra. As três decisões vizinhas registram o
 * que o dono decidiu, e a peça continua onde está; esta a tira da fila na hora.
 * Descobrir isso depois, com a fila uma peça mais curta, é tarde.
 */
const MOTIVO_DA_REGENERACAO =
  'a versão atual é descartada e a esteira escreve outra: a peça sai da fila e volta em alguns minutos';

const COPY = {
  approve: {
    label: 'Justificativa da aprovação, obrigatória',
    placeholder: 'Ex.: o roteiro é parecido, mas o ângulo e a prova são outros',
    confirm: 'Confirmar aprovação',
    empty: 'Escreva a justificativa antes de aprovar.',
    max: MAX_APPROVE_JUSTIFICATION,
  },
  change: {
    label: 'Comentário obrigatório',
    placeholder: 'Ex.: troque o exemplo e reduza o texto do quadro 2',
    confirm: 'Enviar pedido',
    empty: 'Escreva o motivo da alteração antes de enviar.',
    max: MAX_CHANGE_COMMENT,
  },
  reject: {
    label: 'Motivo da rejeição, obrigatório',
    placeholder: 'Ex.: a prova citada não sustenta a afirmação do quadro 1',
    confirm: 'Confirmar rejeição',
    empty: 'Escreva o motivo da rejeição antes de confirmar.',
    max: MAX_REJECT_COMMENT,
  },
  regenerate: {
    label: 'Motivo da regeneração, obrigatório',
    placeholder: 'Ex.: o gancho ficou vago e o exemplo não é do público certo',
    confirm: 'Confirmar',
    empty: 'Escreva o motivo da regeneração antes de confirmar.',
    max: MAX_REGENERATE_REASON,
  },
} as const;

/**
 * As três decisões supervisionadas. Todas caem quando a operação está pausada,
 * e a queda é imediata: bloquear ação irreversível não espera a rede.
 *
 * As três dividem o mesmo campo de propósito. São a mesma pergunta ao revisor,
 * `por quê`, e campos separados só dariam chance de escrever num e enviar pelo
 * outro.
 *
 * A caixa continua nascendo FECHADA, e abre só depois do clique. Isso não é
 * economia de espaço: a tela inicial da página Hoje é congelada por hash
 * (`docs/ui-reference/`, `docs/runbooks/panel-visual-review.md`) e um campo a
 * mais na renderização padrão mudaria o baseline sem aprovação do dono.
 */
export const DecisionForm = ({
  piece,
  changeBoxOpen,
  onOpenChangeBox,
  onCloseChangeBox,
  rascunho = null,
}: {
  piece: ContentReviewDto;
  changeBoxOpen: boolean;
  onOpenChangeBox: () => void;
  onCloseChangeBox: () => void;
  /**
   * Um começo de comentário, vindo de fora.
   *
   * Existe para o pedido de ajuste de um SLIDE do carrossel: o botão do
   * navegador de slides manda `"Slide 4: "` e abre esta caixa. É âncora, e não
   * pedido pronto — quem escreve o que precisa mudar é o dono.
   *
   * `null` é o caso de sempre, e nele nada muda: o campo continua nascendo
   * vazio, e o desenho congelado da tela sai igual.
   */
  rascunho?: string | null;
}) => {
  const {
    paused,
    approveContent,
    requestChange,
    rejectContent,
    regenerateContent,
    showToast,
  } = usePanel();
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState<Decision>('change');
  const [changeTopic, setChangeTopic] = useState(false);
  const [busy, setBusy] = useState(false);
  const commentId = useId();
  const changeTopicId = useId();
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Trocar de peça descarta o rascunho: comentário escrito para uma peça não
  // pode ser enviado sobre outra. A troca de assunto volta a desmarcada pelo
  // mesmo motivo, e porque ela é a metade mais cara do pedido: herdá-la de
  // outra peça mandaria a esteira procurar pauta nova sem ninguém ter pedido.
  useEffect(() => {
    setComment('');
    setChangeTopic(false);
    setDecision('change');
  }, [piece.contentVersionId]);

  useEffect(() => {
    if (changeBoxOpen) {
      commentRef.current?.focus();
    }
  }, [changeBoxOpen, decision]);

  /*
   * O rascunho entra no campo, e NUNCA por cima do que o dono escreveu.
   *
   * A condição é o campo estar vazio. Sem ela, clicar em "pedir ajuste no
   * slide 5" depois de escrever meia frase apagaria a frase, e o dono
   * perceberia isso depois de enviar.
   *
   * `decision` volta para `change` porque o pedido de ajuste é essa decisão: o
   * botão do slide abre a caixa, e a caixa precisa estar na pergunta certa.
   */
  const rascunhoAplicado = useRef<string | null>(null);
  useEffect(() => {
    /*
     * O rascunho é aplicado UMA vez por valor, e nunca por cima do que já
     * existe.
     *
     * A marca em `rascunhoAplicado` é o que faz "pedir ajuste no slide 4" duas
     * vezes seguidas não reescrever o campo que o dono já editou entre os dois
     * cliques. Ela é `ref` e não estado porque não desenha nada: mudá-la não
     * pode redesenhar a caixa no meio da digitação.
     */
    if (rascunho === null || rascunhoAplicado.current === rascunho) {
      return;
    }
    rascunhoAplicado.current = rascunho;
    setComment((atual) => (atual === '' ? rascunho : atual));
    setDecision('change');
  }, [rascunho]);

  const disabled = paused || busy;
  const copy = COPY[decision];
  const trimmed = comment.trim();
  const textoValido =
    trimmed.length >= MIN_COMMENT && trimmed.length <= copy.max;

  /**
   * Alerta em qualquer um dos sete portões.
   *
   * É a mesma condição que o domínio usa para exigir ressalva (`hasAnyAlert` em
   * `refuseApproval`). Ler o desfecho aqui não substitui a cerca do servidor:
   * serve para a tela PEDIR o que a API vai cobrar, em vez de mandar a
   * aprovação, tomar `428` e devolver ao dono um toast sem campo de resposta.
   */
  const temAlerta = piece.gateResults.some(
    (portao) => portao.outcome === 'alert',
  );

  const open = (next: Decision) => {
    if (disabled) {
      return;
    }
    setDecision(next);
    onOpenChangeBox();
  };

  const enviarAprovacao = (justification: string | null) => {
    setBusy(true);
    void approveContent(piece.contentVersionId, justification)
      .then(() => {
        setComment('');
        onCloseChangeBox();
      })
      .catch(() => {
        // O erro já foi anunciado e os contadores já voltaram no provider.
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const onApprove = () => {
    if (disabled) {
      return;
    }
    // Peça com alerta não é enviada neste clique: o clique abre a caixa da
    // ressalva. Peça limpa segue aprovando com um clique só, como sempre.
    if (temAlerta) {
      open('approve');
      return;
    }
    enviarAprovacao(null);
  };

  /**
   * Cancelar fecha a caixa e apaga o rascunho, inclusive a caixa de assunto.
   *
   * Só a regeneração tem este botão. As três decisões fecham a caixa trocando
   * de peça ou concluindo; aqui existe um estado a mais, a troca de assunto, e
   * deixá-lo marcado por baixo de uma caixa fechada é o tipo de coisa que
   * reaparece no clique seguinte sem ninguém ter pedido.
   */
  const onCancel = () => {
    setComment('');
    setChangeTopic(false);
    onCloseChangeBox();
  };

  const onConfirm = () => {
    if (disabled) {
      return;
    }
    const problem =
      trimmed.length === 0
        ? copy.empty
        : trimmed.length < MIN_COMMENT
          ? `Escreva pelo menos ${MIN_COMMENT} caracteres explicando a decisão.`
          : trimmed.length > copy.max
            ? `O motivo passa de ${copy.max} caracteres. Resuma.`
            : null;

    if (problem) {
      showToast(problem, 'error');
      commentRef.current?.focus();
      return;
    }

    if (decision === 'approve') {
      enviarAprovacao(trimmed);
      return;
    }

    const send =
      decision === 'reject'
        ? rejectContent(piece.contentVersionId, trimmed)
        : decision === 'regenerate'
          ? regenerateContent(piece.contentVersionId, trimmed, changeTopic)
          : requestChange(piece.contentVersionId, trimmed);

    setBusy(true);
    void send
      .then(() => {
        setComment('');
        setChangeTopic(false);
        onCloseChangeBox();
      })
      .catch(() => {
        // Idem: o provider já anunciou e já desfez.
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <div className="dm-actions">
        <button
          className="dm-action dm-action-primary"
          type="button"
          disabled={disabled}
          // Sem alerta o botão não expande nada, e anunciar `aria-expanded`
          // ali prometeria uma caixa que não vai abrir.
          aria-expanded={
            temAlerta ? changeBoxOpen && decision === 'approve' : undefined
          }
          onClick={onApprove}
        >
          Aprovar peça
        </button>
        <button
          className="dm-action"
          type="button"
          disabled={disabled}
          aria-expanded={changeBoxOpen && decision === 'change'}
          onClick={() => {
            open('change');
          }}
        >
          Pedir alteração
        </button>
        <button
          className="dm-action dm-action-danger"
          type="button"
          disabled={disabled}
          aria-expanded={changeBoxOpen && decision === 'reject'}
          onClick={() => {
            open('reject');
          }}
        >
          Rejeitar peça
        </button>
        <button
          className="dm-action"
          type="button"
          disabled={disabled}
          aria-expanded={changeBoxOpen && decision === 'regenerate'}
          onClick={() => {
            open('regenerate');
          }}
        >
          Regenerar peça
        </button>
      </div>

      {changeBoxOpen ? (
        <div className="dm-change-box">
          {decision === 'approve' ? (
            <p className="dm-change-reason">{MOTIVO_DA_RESSALVA}</p>
          ) : null}
          {decision === 'regenerate' ? (
            <p className="dm-change-reason">{MOTIVO_DA_REGENERACAO}</p>
          ) : null}
          <label htmlFor={commentId}>{copy.label}</label>
          <textarea
            id={commentId}
            ref={commentRef}
            value={comment}
            maxLength={copy.max}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            placeholder={copy.placeholder}
          />
          {decision === 'regenerate' ? (
            <div className="dm-change-toggle">
              <input
                id={changeTopicId}
                type="checkbox"
                checked={changeTopic}
                disabled={disabled}
                onChange={(event) => {
                  setChangeTopic(event.target.checked);
                }}
              />
              <label htmlFor={changeTopicId}>
                Trocar o assunto (pauta nova)
              </label>
            </div>
          ) : null}
          <div className="dm-actions">
            <button
              className={
                decision === 'reject'
                  ? 'dm-action dm-action-danger'
                  : 'dm-action dm-action-primary'
              }
              type="button"
              // Só a aprovação trava o botão por texto: ela é a decisão que
              // LIBERA a peça, e um clique sem ressalva vira `428` da API. Nas
              // outras duas o botão continua clicável e o erro é dito em voz
              // alta pelo toast, que é o comportamento que elas já tinham.
              disabled={disabled || (decision === 'approve' && !textoValido)}
              onClick={onConfirm}
            >
              {copy.confirm}
            </button>
            {decision === 'regenerate' ? (
              <button
                className="dm-action"
                type="button"
                disabled={disabled}
                onClick={onCancel}
              >
                Cancelar
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};
